import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { format } from "date-fns";
import { Trash2, Plus, MessageSquare } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { getProfileNotes, createProfileNote, deleteProfileNote, sendProfileActualizationEmail, updateProfileMetadataStatus, getProfileMetadata } from "@/service/weexpertService";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { toast } from "../ui/use-toast";
import { SelectWithAlert } from "../ui/select-with-alert";

const getInitials = (firstName, lastName) => {
  return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
};

const statusOptions = [
  { value: "Uncontacted", label: "Uncontacted" },
  { value: "Contacted", label: "Contacted" },
  { value: "ProfileUpdate", label: "Profile Update" },
  { value: "NotInterested", label: "Not Interested" },
  { value: "Active", label: "Active" }
];

export function ProfileNotes({  clientId, profile, profileUpdatedAt }) {
  console.log({profile})
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [newNote, setNewNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [profileStatus, setProfileStatus] = useState("Uncontacted");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const { data: notes, isLoading, error, refetch } = useQuery({
    queryKey: ["profileNotes", clientId],
    queryFn: () => getProfileNotes(clientId),
    enabled: !!clientId,
  });

  const { data: metadata, isLoading: isLoadingMetadata } = useQuery({
    queryKey: ["profileMetadata", clientId],
    queryFn: () => getProfileMetadata(clientId),
    enabled: !!clientId,
    onSuccess: (data) => {
      console.log("Profile metadata fetched:", data);
    }
  });

  const createNoteMutation = useMutation(
    (noteText) => createProfileNote(clientId, noteText)
  );

  const deleteNoteMutation = useMutation(
    (noteId) => deleteProfileNote(noteId),
    {
      onSuccess: () => {
        refetch();
      },
      onError: (error) => {
        console.error("Error deleting note:", error);
      },
    }
  );

  useEffect(() => {
    if (metadata?.data?.status) {
      setProfileStatus(metadata.data.status);
    }
  }, [metadata]);

  const isEmailSentRecently = () => {
    if (!metadata?.data?.lastUpdateProfileEmailSend) return false;

    const lastEmailDate = new Date(metadata.data.lastUpdateProfileEmailSend);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    return lastEmailDate > sevenDaysAgo;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newNote.trim()) {
      setIsSubmitting(true);
      try {
        await createNoteMutation.mutateAsync(newNote.trim());
        await refetch();
        setNewNote("");
      } catch (error) {
        console.error("Error creating note:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleDelete = (noteId) => {
    if (window.confirm(t("alerts:confirm_delete"))) {
      deleteNoteMutation.mutate(noteId);
    }
  };

  const handleSendProfileUpdateEmail = async () => {
    setIsSendingEmail(true);
    try {
      await sendProfileActualizationEmail(clientId);
      toast({
        title: t("alerts:toast_profile_success"),
      });
    } catch (error) {
      console.error("Error sending profile update email:", error);
      toast({
        title: t("alerts:toast_profile_error"),
        variant: "destructive",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    const previousStatus = profileStatus;
    setProfileStatus(newStatus);
    setIsUpdatingStatus(true);

    try {
      await updateProfileMetadataStatus(clientId, newStatus);
      toast({
        title: t("alerts:toast_update_profile_success"),
      });
    } catch (error) {
      console.error("Error updating profile status:", error);
      setProfileStatus(previousStatus);
      toast({
        title: t("alerts:toast_update_profile_error"),
        variant: "destructive",
      });
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 p-4">
        {t("common:error_loading_data")}
      </div>
    );
  }

  const notesData = notes?.data || [];

  return (
    <div className="flex flex-col h-full space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blueLightAccent flex items-center justify-center ">
            <MessageSquare className="h-5 w-5 text-blueText" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">{t("common:notes")}</h2>
            <p className="text-sm text-muted-foreground">
              {notesData.length} {notesData.length === 1 ? t("common:note") : t("common:notes_plural")}
            </p>
            {profileUpdatedAt && (
              <p className="text-xs text-muted-foreground mt-1">
                {t("common:last_updated")}: {format(new Date(profileUpdatedAt), "dd.MM.yyyy HH:mm")}
              </p>
            )}
            {metadata?.data?.lastUpdateProfileEmailSend && (
              <p className="text-xs text-muted-foreground mt-1">
                {t("last_email_sent")} {format(new Date(metadata.data.lastUpdateProfileEmailSend), "dd.MM.yyyy HH:mm")}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-4 items-center justify-center">
        <div className="w-64">
          <SelectWithAlert
            label={t("profile_Status")}
            options={statusOptions}
            value={profileStatus}
            onChangeValue={handleStatusChange}
            disabled={isUpdatingStatus}
            placeholder="Select status"
            inputClassName="h-10 bg-white"
            modal={false}
          />
        </div>
        <div className="pt-6">
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Div need for tooltip to worik with the */}
              <div>

                <Button
                  className={"h-10"}
                  disabled={!Boolean(profile.marketing) || isSendingEmail || isEmailSentRecently()}
                  onClick={handleSendProfileUpdateEmail}
                >
                  {isSendingEmail ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                      {t("sending")}
                    </>
                  ) : (
                    t("button_ask_update")
                  )}
                </Button>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {!Boolean(profile.marketing)
                  ? t("no_emails")
                  : isEmailSentRecently()
                    ? t("wait_message")
                    : t("send_email_message")
                }
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
      {/* Add Note Form */}
      <Card className="p-5 shadow-sm border-border/60">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="note-input" className="text-sm font-medium mb-2 block">
              {t("common:add_new_note")}
            </label>
            <Textarea
              id="note-input"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder={t("common:add_note_placeholder")}
              className="min-h-[120px] resize-none focus-visible:ring-2 focus-visible:ring-blueText/20 transition-all"
              disabled={isSubmitting}
            />
          </div>
          <Button
            type="submit"
            disabled={!newNote.trim() || isSubmitting}
            className="w-full sm:w-auto gap-2 shadow-sm bg-blueLightAccent text-blueText border border-blueBorder hover:bg-blueLightAccent"
          >
            {isSubmitting ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                {t("common:saving")}
              </>
            ) : (
              <>
                <Plus className="h-4 w-4" />
                {t("common:add_note")}
              </>
            )}
          </Button>
        </form>
      </Card>

      {/* Notes List */}
      <div className="flex-1 overflow-hidden">
        <div className="space-y-3 overflow-y-auto max-h-[600px] pr-2">
          {notesData.length > 0 ? (
            notesData.map((note) => (
              <Card
                key={note.id}
                className="p-5 shadow-sm border-border/60 hover:border-blueText/30 hover:shadow-md transition-all duration-200 group"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Avatar className="w-8 h-8 flex-shrink-0">
                      <AvatarFallback className="bg-blueLightAccent text-blueText text-xs font-medium">
                        {getInitials(note.user?.firstName, note.user?.lastName)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        {note.user?.firstName} {note.user?.lastName}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(note.createdAt), "dd.MM.yyyy HH:mm")}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(note.id)}
                    className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors flex-shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{note.note}</p>
              </Card>
            ))
          ) : (
            <Card className="p-12 border-dashed border-2">
              <div className="flex flex-col items-center justify-center text-center gap-3">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                  <MessageSquare className="h-8 w-8 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium text-foreground mb-1">{t("common:no_notes_yet")}</p>
                  <p className="text-sm text-muted-foreground">{t("common:start_adding_first_note")}</p>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
